<script setup>
import BenefitCards from '@/Components/BenefitCards.vue';
import GoogleAutocomplete from '@/Components/GoogleAutocomplete.vue';
import PartnerList from '@/Components/PartnerList.vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { trans as $t } from 'laravel-vue-i18n';
import Swal from 'sweetalert2';
import PromoDialog from '@/Components/PromoDialog.vue';
</script>

<script>
export default {

    props: {
        bounds: {
            type: Object,
            required: true
        },
        carTypes: {
            type: Array
        },
        currency: {
            type: String
        },
        currentPromotion: {
            type: String,
            default: null
        },
        errors: {
            type: Object
        },
        promotionCoupon: {
            type: String
        },
        test: {
            type: String
        },
        user: {
            type: Object
        }
    },

    data() {
        return {
            ajaxRequest: false,
            orderData: {
                transferType: 'AC',
                tripType: 'return',
                selectedAddress: null,
                selectedAddressText: 'Budapest, ',
                adults: 1,
                children: 0,
                totalPrice: {
                    HUF: 0,
                    EUR: 0
                },
            },
        };
    },

    methods: {

        setAddress(newAddress, inputAddress) {
            this.orderData.selectedAddress = newAddress;
            this.orderData.selectedAddressText = inputAddress;
        },

        calculatePrice() {

            const that = this;

            if (!this.orderData.selectedAddress?.placeId) {
                this.orderData.totalPrice = {
                    HUF: 0,
                    EUR: 0,
                }
                return;
            }

            router.post(route('calculatePrice'), this.orderData, {
                only: ['price', 'errors'],
                // method: 'get',
                preserveState: true,
                preserveScroll: true,
                onStart: visit => {
                    this.ajaxRequest = true;
                },
                onSuccess: page => {
                    if (page.props?.price) {
                        this.orderData.totalPrice = page.props.price;
                    }
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                    this.orderData.totalPrice = {
                        HUF: 0,
                        EUR: 0,
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        getTotalPrice(currency) {
            // Divide the price by 2 for return trips to show the one-way price
            if (this.orderData.tripType == 'return') {
                const oneWayPrice = Math.round((currency == 'HUF' ? this.orderData.totalPrice.HUF / 2 : this.orderData.totalPrice.EUR / 2) * 100) / 100;
                return new Intl.NumberFormat().format(oneWayPrice) + ' ' + currency + ' ' + this.$t('irányonként');
            }

            // Just format the total price for one-way trips
            return new Intl.NumberFormat().format(currency == 'HUF' ? this.orderData.totalPrice.HUF : this.orderData.totalPrice.EUR) + ' ' + currency;
        },

        startBooking() {
            const that = this;
            that.ajaxRequest = true;
            router.post(route('startBooking'), this.orderData, {
                only: ['errors'],
                // Do not scroll this page if we have errors
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: page => {
                    if (!page.props.errors.length) {
                        if (dataLayer) {
                            dataLayer.push({
                                event: 'select_item',
                                item_name: 'AIRPORT SHUTTLE | ' + (this.orderData.tripType == 'return' ? 'Return' : 'One-way') + ' | adult ' + this.orderData.adults + ' - underAge2 ' + this.orderData.children,
                                item_category: 'Transfer shuttle',
                                transfer_type: (this.orderData.tripType == 'return' ? 'Return transfer' : 'One-way transfer'),
                                transfer_direction: this.orderData.transferType == 'AC' ? 'Airport to city' : 'City to airport',
                                adult_passangers_number: this.orderData.adults,
                                passangers_underage2_number: this.orderData.children,
                            });
                        }
                    }
                },
                onFinish: visit => {
                    that.ajaxRequest = false;
                },
            });
        },

        gtagCarType(type) {
            dataLayer.push({
                event: 'select_item',
                item_category: 'Transfer shuttle',
                bus_type: type,
            });
        },

        gtagExtraProduct(type) {
            dataLayer.push({
                event: 'select_item',
                item_category: 'Extra product',
                item_name: type,
            });
        }

    },

    watch: {

        'orderData.selectedAddress': {
            handler(newValue, oldValue) {
                this.calculatePrice();
            },
            deep: true,
            // immediate: true,
        },

        'orderData.tripType': {
            handler(newValue, oldValue) {
                this.calculatePrice();
            },
        },

        'orderData.adults': {
            handler(newValue, oldValue) {
                this.calculatePrice();
            },
        },

        'orderData.children': {
            handler(newValue, oldValue) {
                this.calculatePrice();
            },
        },

    },

    mounted() {

        if (this.user) {
            if (this.user.isHotel) {
                this.orderData.selectedAddress = {
                    placeId: '-',
                    displayName: this.user.displayName,
                    country: 'Magyarország',
                    district: '-',
                    postcode: this.user.postcode,
                    city: this.user.city,
                    street: this.user.street,
                    houseNr: this.user.houseNr,
                    poi: false,
                    latitude: this.user.latitude,
                    longitude: this.user.longitude
                }
                this.orderData.selectedAddressText = this.user.displayName;
            }
        }

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'main',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }
};
</script>

<style scoped>
.masthead {
    min-height: 100vh;
    background-image: url('../../images/header.webp');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

@media (max-width: 992px) {
    .masthead {
        background-image: url('../../images/header-mobile.webp');
        background-size: cover;
        background-position: center;
    }

}

.no-webp .masthead {
    background-image: url('../../images/header.jpg');
}

@media (max-width: 992px) {
    .no-webp .masthead {
        background-image: url('../../images/header-mobile.jpg');
    }

}

.cloud {
    background-image: url('../../images/cloud.webp');
    background-position: center bottom;
    background-repeat: no-repeat;
    min-height: 100vh;
}

@media (min-width: 1920px) {
    .cloud {
        background-size: cover;
    }
}

@media (min-width: 992px) {
    .home-calculation-form {
        margin-top: 60vh !important;
        padding: 0;
    }
}

.no-webp .cloud {
    background-image: url('../../images/cloud.png');
}

@media (max-width: 768px) {
    .no-webp .cloud {
        background-image: url('../../images/cloud-mobile.png');
    }
}
</style>

<template>

    <Head :title="$t('Reptéri transzfer foglalás')" />

    <AppLayout>

        <PromoDialog v-if="currentPromotion" :folderName="currentPromotion" :promoLink="route('booking')" />

        <header class="masthead">
            <div class="cloud">
                <div class="container-lg h-100">
                    <div class="row h-100 align-items-center">
                        <div class="col-12">
                            <div class="card home-calculation-form text-bg-secondary" style="--bs-bg-opacity: .9">
                                <div class="card-body pb-0">
                                    <h1 class="card-title mt-0 mb-3 mb-md-4 text-center">{{ $t('Reptéri transzfer foglalás') }}</h1>
                                    <form autocomplete="off">
                                        <div class="row">
                                            <div class="col-md-6">
                                                <div class="btn-group w-100 mb-3" role="group" :aria-label="$t('Transzfer típusa')">
                                                    <input type="radio" class="btn-check" name="transferType" id="airport-city" value="AC" v-model="orderData.transferType" autocomplete="off" />
                                                    <label class="btn btn-outline-primary text-white w-100" for="airport-city">
                                                        <span class="mb-2">
                                                            <i class="fal fa-plane-arrival me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-city me-1"></i>
                                                        </span>
                                                        <span>{{ $t('Reptérről a városba') }}</span>
                                                    </label>
                                                    <input type="radio" class="btn-check" name="transferType" id="city-airport" value="CA" v-model="orderData.transferType" autocomplete="off" />
                                                    <label class="btn btn-outline-primary text-white w-100" for="city-airport">
                                                        <span class="mb-2">
                                                            <i class="fal fa-city me-1"></i> <i class="fal fa-long-arrow-right me-1"></i> <i class="fal fa-plane-departure me-1"></i>
                                                        </span>
                                                        <span>{{ $t('Városból a reptérre') }}</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                <div class="btn-group w-100 mb-3" role="group" :aria-label="$t('Útirány')">
                                                    <input type="radio" class="btn-check" name="tripType" id="return" value="return" v-model="orderData.tripType" autocomplete="off" />
                                                    <label class="btn btn-outline-primary text-white w-100" for="return">
                                                        <span><i class="fal fa-exchange me-1"></i></span>
                                                        <span>{{ $t('Retúr transzfer') }}</span>
                                                    </label>
                                                    <input type="radio" class="btn-check" name="tripType" id="one-way" value="oneWay" v-model="orderData.tripType" autocomplete="off" />
                                                    <label class="btn btn-outline-primary text-white w-100" for="one-way">
                                                        <span><i class="fal fa-long-arrow-right me-1"></i></span>
                                                        <span>{{ $t('Egyirányú transzfer') }}</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                <div class="form-floating mb-3">
                                                    <template v-if="user?.isHotel">
                                                        <input type="text" class="form-control form-control-transparent" :class="{ 'is-invalid': errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }" id="address" :placeholder="$t('Hotel')" v-model="orderData.selectedAddressText" readonly>
                                                        <label for="name">{{ $t('Hotel') }}<span class="text-danger">*</span></label>
                                                        <div class="invalid-feedback" v-if="errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName']">{{
                                                            errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }}</div>
                                                    </template>
                                                    <template v-else>
                                                        <GoogleAutocomplete id="address" :placeholder="$t('Hotel vagy cím')" class="form-control form-control-transparent" :class="{ 'is-invalid': errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }" :bounds-north="bounds.north" :bounds-east="bounds.east" :bounds-south="bounds.south" :bounds-west="bounds.west" :value="orderData.selectedAddressText" @address-changed="setAddress" />
                                                        <div class="invalid-feedback" v-if="errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName']">
                                                            {{ errors.selectedAddress || errors['selectedAddress.placeId'] || errors['selectedAddress.displayName'] }}
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                            <div class="col-6 col-md-3">
                                                <div class="form-floating mb-3">
                                                    <input type="number" class="form-control form-control-transparent" :class="{ 'is-invalid': errors.adults }" id="adults" :placeholder="$t('Felnőtt utas')" min="1" max="99" v-model="orderData.adults">
                                                    <label for="adults">{{ $t('Felnőtt utas') }}</label>
                                                    <div class="invalid-feedback" v-if="errors.adults">{{ errors.adults }}</div>
                                                </div>
                                            </div>
                                            <div class="col-6 col-md-3">
                                                <div class="form-floating mb-3">
                                                    <input type="number" class="form-control form-control-transparent" :class="{ 'is-invalid': errors.children }" id="children" :placeholder="$t('2 éven aluli utas')" min="0" max="99" v-model="orderData.children">
                                                    <label for="children">{{ $t('2 éven aluli utas') }}</label>
                                                    <div class="invalid-feedback" v-if="errors.children">{{ errors.children }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div class="card-footer border-0">
                                    <div class="row align-items-center">
                                        <div class="col-12 col-md-6 text-center text-md-start mb-3 mb-md-0">
                                            <span v-if="errors.price" class="invalid-feedback d-block">{{ errors.price }}</span>
                                            <span v-else-if="!orderData.totalPrice.HUF">{{ $t('Kérjük, adja meg a transzfer foglaláshoz szükséges összes adatot!') }}</span>
                                            <span v-else-if="!ajaxRequest" class="price shadow">{{ $t('A transzfer ára') }}: {{ getTotalPrice(currency) }}</span>
                                        </div>
                                        <div class="col-12 col-md-6 text-center text-md-end mb-3 mb-md-0">
                                            <button type="button" class="btn btn-primary text-uppercase" :disabled="ajaxRequest" @click="startBooking">
                                                {{ $t('Folytatás') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <BenefitCards class="mt-3" />

                        </div>
                    </div>
                </div>
            </div>
        </header>

        <main class="main">

            <section class="section">
                <div class="container">
                    <div class="row section-title">
                        <div class="col-12">
                            <h2>{{ $t('Reptéri transzfer járműtípus alapján') }}</h2>
                            <h3>{{ $t('Mi az Ön igényeinek megfelelően alakítjuk ki a szállítási feltételeinket!') }}</h3>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="card-group text-center">
                                <div class="card border-0 mb-3 mb-md-0 align-items-center" v-for="carType in carTypes">
                                    <picture>
                                        <source :srcset="carType.imageWebp" type="image/webp">
                                        <source :srcset="carType.image" type="image/png">
                                        <img :src="carType.image" width="200" height="127" :alt="$t(carType.properties.displayName)" loading="lazy">
                                    </picture>
                                    <div class="card-title card-title-50 text-uppercase">{{ $t(carType.properties.displayName) }}</div>
                                    <div class="card-body pt-0">
                                        <p class="card-text" v-html="$t(carType.description)"></p>
                                    </div>
                                    <div class="card-footer border-0 bg-transparent">
                                        <Link :href="route('startBookingByCarType')" method="post" :data="{ carType: carType.name }" as="button" class="btn btn-primary btn-block text-uppercase" @click="gtagCarType(carType.name)">{{ $t('Foglalás most') }}</Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="section">
                <div class="container">
                    <div class="row section-title">
                        <div class="col-12">
                            <h2>{{ $t('Promócióink az Ön igényei szerint') }}</h2>
                            <h3>{{ $t('Ismerjük utasaink utazási szokásait, és ahol tudunk, segítünk.') }}'</h3>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div id="couponCarousel" class="carousel slide" data-bs-ride="carousel">
                                <div class="carousel-indicators">
                                    <button type="button" data-bs-target="#couponCarousel" data-bs-slide-to="0" class="active" aria-current="true" :aria-label="$t('Promóció szöveg 1')"></button>
                                    <button type="button" data-bs-target="#couponCarousel" data-bs-slide-to="1" :aria-label="$t('Promóció szöveg 2')"></button>
                                    <button v-if="$page.props.promotionCoupon" type="button" data-bs-target="#couponCarousel" data-bs-slide-to="2" :aria-label="$t('Promóció szöveg 3')"></button>
                                </div>
                                <div class="carousel-inner text-center">
                                    <div class="carousel-item active">
                                        <Link :href="route('startBookingByPromotion')" method="post" :data="{ nr: 1 }" as="button" class="btn">
                                            <template v-if="$page.props.locale == 'hu'">
                                                <picture>
                                                    <source srcset="../../images/promotions/promotion-1-hu.webp" type="image/webp">
                                                    <source srcset="../../images/promotions/promotion-1-hu.png" type="image/png">
                                                    <img src="../../images/promotions/promotion-1-hu.png" :alt="$t('Promóció szöveg 1')" class="img-fluid" width="848" height="457" loading="lazy">
                                                </picture>
                                            </template>
                                            <template v-else>
                                                <source srcset="../../images/promotions/promotion-1-en.webp" type="image/webp">
                                                <source srcset="../../images/promotions/promotion-1-en.png" type="image/png">
                                                <img src="../../images/promotions/promotion-1-en.png" :alt="$t('Promóció szöveg 1')" class="img-fluid" width="848" height="457" loading="lazy">
                                            </template>
                                        </Link>
                                    </div>
                                    <div class="carousel-item">
                                        <Link :href="route('startBookingByPromotion')" method="post" :data="{ nr: 2 }" as="button" class="btn">
                                            <template v-if="$page.props.locale == 'hu'">
                                                <picture>
                                                    <source srcset="../../images/promotions/promotion-2-hu.webp" type="image/webp">
                                                    <source srcset="../../images/promotions/promotion-2-hu.png" type="image/png">
                                                    <img src="../../images/promotions/promotion-2-hu.png" :alt="$t('Promóció szöveg 2')" class="img-fluid" width="848" height="457" loading="lazy">
                                                </picture>
                                            </template>
                                            <template v-else>
                                                <source srcset="../../images/promotions/promotion-2-en.webp" type="image/webp">
                                                <source srcset="../../images/promotions/promotion-2-en.png" type="image/png">
                                                <img src="../../images/promotions/promotion-2-en.png" :alt="$t('Promóció szöveg 2')" class="img-fluid" width="848" height="457" loading="lazy">
                                            </template>
                                        </Link>
                                    </div>
                                    <div class="carousel-item" v-if="$page.props.promotionCoupon">
                                        <Link :href="route('startBookingByPromotion')" method="post" :data="{ nr: 3 }" as="button" class="btn">
                                            <template v-if="$page.props.locale == 'hu'">
                                                <picture>
                                                    <source srcset="../../images/promotions/promotion-3-hu.webp" type="image/webp">
                                                    <source srcset="../../images/promotions/promotion-3-hu.png" type="image/png">
                                                    <img src="../../images/promotions/promotion-3-hu.png" :alt="$t('Promóció szöveg 3')" class="img-fluid" width="848" height="457" loading="lazy">
                                                </picture>
                                            </template>
                                            <template v-else>
                                                <source srcset="../../images/promotions/promotion-3-en.webp" type="image/webp">
                                                <source srcset="../../images/promotions/promotion-3-en.png" type="image/png">
                                                <img src="../../images/promotions/promotion-3-en.png" :alt="$t('Promóció szöveg 3')" class="img-fluid" width="848" height="457" loading="lazy">
                                            </template>
                                        </Link>
                                    </div>
                                </div>
                                <button class="carousel-control-prev d-none d-md-block" type="button" data-bs-target="#couponCarousel" data-bs-slide="prev">
                                    <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                                    <span class="visually-hidden">{{ $t('Előző') }}</span>
                                </button>
                                <button class="carousel-control-next d-none d-md-block" type="button" data-bs-target="#couponCarousel" data-bs-slide="next">
                                    <span class="carousel-control-next-icon" aria-hidden="true"></span>
                                    <span class="visually-hidden">{{ $t('Következő') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="section" v-if="false">
                <div class="container">
                    <div class="row section-title">
                        <div class="col-12">
                            <h2>{{ $t('Foglalásához számos extra szolgáltatást is hozzáadhat') }}</h2>
                            <h3>{{ $t('Kényelme fontos számunkra, ezért extra kényelmi szolgáltatásokat is kínálunk.') }}'
                            </h3>
                        </div>
                    </div>
                    <div class="row">

                        <div class="col-12">
                            <div class="card-group text-center">
                                <div class="card px-4 pt-4 pb-0 border-0 section-lightgray">
                                    <div>
                                        <picture>
                                            <source srcset="../../images/bpcard-24.webp" type="image/webp" />
                                            <source srcset="../../images/bpcard-24.png" type="image/png" />
                                            <img src="../../images/bpcard-24.png" alt="Budapest Card" width="250" height="119" loading="lazy" />
                                        </picture>
                                    </div>
                                    <div class="card-title mb-0">{{ $t('Budapest Card') }}</div>
                                    <div class="card-body">
                                        <div class="truncate-text" data-read-smore-words="30" v-html="$t('A Budapest Card egy városkártya, melynek megvásárlásával éjjel-nappal ingyenesen használható Budapesten valamennyi tömegközlekedési eszköz és bizonyos turisztikai szolgáltatások, valamint számos helyen biztosít kedvezményt! A Budapest Card 24, 48, 72, 96 és 120 órás verzióban érhető el. Minden kártya 30+ ingyenes szolgáltatást tartalmaz az alábbi kiemelteken kívül:')">
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col-12 col-md-4">
                                            <Link :href="route('services')" class="btn btn-secondary w-100 mb-4" @click="gtagExtraProduct('Budapest Card (24 órás)')">{{ $t('24 óra') }}</Link>
                                        </div>
                                        <div class="col-12 col-md-4">
                                            <Link :href="route('services')" class="btn btn-secondary w-100 mb-4" @click="gtagExtraProduct('Budapest Card (48 órás)')">{{ $t('48 óra') }}</Link>
                                        </div>
                                        <div class="col-12 col-md-4">
                                            <Link :href="route('services')" class="btn btn-secondary w-100 mb-4" @click="gtagExtraProduct('Budapest Card (72 órás)')">{{ $t('72 óra') }}</Link>
                                        </div>
                                    </div>
                                </div>
                                <div class="card px-4 pt-4 pb-0 border-0 section-lightgray">
                                    <div>
                                        <picture>
                                            <source srcset="../../images/bpcard-72-plus.webp" type="image/webp" />
                                            <source srcset="../../images/bpcard-72-plus.png" type="image/png" />
                                            <img src="../../images/bpcard-72-plus.png" alt="Budapest Card Plus" width="250" height="119" loading="lazy" />
                                        </picture>
                                    </div>
                                    <div class="card-title mb-0">{{ $t('Budapest Card Plus') }}</div>
                                    <div class="card-body">
                                        <div class="truncate-text" data-read-smore-words="30" v-html="$t('A BUDAPEST CARD termékcsalád 30+ ingyenes szolgáltatásán túl, ingyenes háztól-házig reptéri transzfer, ingyenes hajózás a Dunán, ingyenes belépés a Mátyás-templomba, ingyenes utazás a Budavári Siklón és ingyenes Molnár’s kürtőskalács várja. Ezen felül 100+ kedvezményes attrakció keretében megannyi nevezetességet, fürdőt, kulturális örökséget tekinthet meg, élményekkel teli programokat és a magyar gasztronómiát ismerheti meg. Még sok más új kedvezmény várja ezen felül, csak a BUDAPEST CARD Plus-al.')">
                                        </div>
                                    </div>
                                    <div class="row form-gutter">
                                        <div class="col-12 form-gutter">
                                            <Link :href="route('services')" class="btn btn-secondary w-100 mb-4" @click="gtagExtraProduct('Budapest Card Plus')">{{ $t('Vásárlás most') }}</Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="section section-lightgray">
                <div class="container">
                    <div class="row section-title">
                        <div class="col-12">
                            <h2>{{ $t('Partnereink') }}</h2>
                        </div>
                    </div>
                    <PartnerList />
                </div>
            </section>

        </main>
    </AppLayout>
</template>
